import React from 'react';
import { faChessKnight } from '@fortawesome/free-solid-svg-icons';
import BlogPost from '../BlogPost';
// Create: src/assets/images/blogs/move-37-semantics.jpg
import move37Img from '../../../assets/images/blogs/move-37-semantics.jpg';

const Move37Semantics = () => (
    <BlogPost
        title="AI Native Semantics: Move 37 For Encoding Knowledge"
        subtitle="The assumption that meaning must flow through human abstraction before machines can use it may no longer hold."
        category="Cognition"
        categoryIcon={faChessKnight}
        color="#003366"
        date="February 2026"
        readTime="6 min read"
        image={move37Img}
        imageAlt="Move 37 - AI-native semantics that break conventional ontology assumptions"
        ctaHeading="Ready to see AI-native semantics?"
        ctaText="Book a discovery call and we'll show you how we achieve 80% automated knowledge encoding without human translation layers."
        seoDescription="How AI-native semantics achieves 80% automated encoding by breaking from traditional ontology-first approaches, inspired by AlphaGo's Move 37."
        seoKeywords="AI semantics, ontology, knowledge encoding, ConsistentAI, knowledge flywheel, AlphaGo, Nebulyx"
        slug="move-37-semantics"
    >
        <p>
            Saturday reflections (and probably going to get me in trouble). 🤔
        </p>

        <p>
            Move 37. AlphaGo's legendary move against Lee Sedol was a move no human would
            make. Looked wrong to every expert but won the game.
        </p>

        <p>
            Had my own Move 37 moment recently.
        </p>

        <h3>The Problem</h3>

        <p>
            Extract semantics from regulatory domains and apply to specific companies.
            Different companies, different contexts, same underlying domain.
        </p>

        <p>
            Classic ontology problem, right? So I tried the ontology-first approach.
        </p>

        <p>
            But kept breaking.
        </p>

        <p>
            Not because methods were bad. Because they seemed more designed to build
            libraries, not decisioning systems. There's an issue with the abstraction
            approach I kept hitting.
        </p>

        <h3>The Abstraction Problem</h3>

        <p>
            A regulation has multiple versions. Amendments, additions - which company-specific
            legal interpretations are then layered on top.
        </p>

        <p>
            These compound - i.e. are relevant at the time of that version. So understanding,
            intent, decision, and action are rooted in the specific state at that relevant
            moment.
        </p>

        <p>
            Classic ontology tries to abstract this into universal views with cross-temporal
            entities and stable relationships.
        </p>

        <blockquote className="blog-post-quote">
            "But the point of the regulation isn't the abstraction. For an org it's the
            key decision it needs right now, for this company, under these interpretations."
        </blockquote>

        <p>
            So there's tension there, in objective, which I've run slap bang into.
        </p>

        <h3>The Flip</h3>

        <p>
            So I went back to basics. Our Knowledge Flywheel doesn't abstract meaning for
            humans to interpret later. It encodes domain truth directly. Regulations,
            guidelines, policies and decisions - all versioned &amp; bounded at the
            specific time.
        </p>

        <p>
            I created a new LLM model architecture to solve this tension. Not probabilistic,
            Consistent- question, same answer with full provenance.
        </p>

        <p>
            Information &amp; semantics encoded directly in the model - i.e. no upfront
            human translation layer &amp; no information loss.
        </p>

        <p>
            With our AgentDOG decisioning system, I kept coming back to this: measurement,
            intent, decision, action - they're contextual and specific to the process &amp;
            specific to the point in time. Can't separate intent from execution in practice.
        </p>

        <h3>Move 37</h3>

        <p>
            This felt wrong at first. Every instinct said: you need the ontology. You need
            humans to define meaning first.
        </p>

        <p>
            <strong>But the results said otherwise.</strong> 80% automated upfront without
            human intervention, Consistent&amp; grounded.
        </p>

        <p>
            Humans absolutely need to review and adjust (as not 100%, but close) + control
            &amp; override when wrong.
        </p>

        <p>
            We still need to understand &amp; encode semantics but the assumption that
            meaning must flow through human abstraction before machines can use it may
            no longer hold.
        </p>

        <h3>A New Era</h3>

        <p>
            We are in a new era of AI-native semantics - with meaning encoded directly in
            an AI-native way and rooted in the specific, not abstracted into the general.
        </p>

        <p>
            I'm still on this journey and lots to learn - definitely standing on the
            shoulders of giants - but the results so far are very encouraging. 🎯
        </p>
    </BlogPost>
);

export default Move37Semantics;
